/* @flow */

import "./StoreSelectionDropdown.scss";

import PropTypes from "prop-types";

import * as React from "react";
import * as _ from "lodash";
import { default as Select } from "react-select";
import { FormGroup, Panel } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { CustomerAddressBadgeWithStore } from "./CustomerAddressBadgeWithStore";
import { InputGroup, FormControl, Button } from "react-bootstrap";
import { StoreType } from "../../../../datatypes/Store";
import { FsdAddressType } from "shared/src/types/customer/FsdAddress";

export type DropDownOption = {
    value: {
        type: "store" | "address";
        store: StoreType;
        address?: FsdAddressType;
    };
    label: any;
    className?: string;
    disabled?: boolean;
    isAddress?: boolean;
};

type Props = {
    stores: Array<StoreType>;
    deliveryAddresses: Array<FsdAddressType>;
    onChange: (option: any) => void;
    selectedOption: DropDownOption | undefined | null;
    inMobileMenu: boolean;
};

type State = {
    searchInput: string;
};

export class StoreSelectionDropdown extends React.Component<Props, State> {
    static contextTypes = { intl: PropTypes.object };
    state = { searchInput: "" };

    getStreetAddress(store: StoreType): string {
        return (
            store.addressInformation.street +
            " " +
            store.addressInformation.houseNumber +
            " " +
            store.addressInformation.building
        );
    }

    getCityAndPostalCode(store: StoreType): string {
        return store.addressInformation.city + " " + store.addressInformation.postalCode;
    }

    filterOption(option: DropDownOption, filter: string): boolean {
        return this.filterStore(option.value.store, _.escapeRegExp(filter));
    }

    getStoreId(store: StoreType): string {
        return _.get(store, ["storeId", "locationNumber"], "");
    }

    filterStore(store: StoreType, filter: string): boolean {
        let searchString = (store.storeDisplayName ? store.storeDisplayName : "") + " " + this.getStoreId(store);
        if (!_.isEmpty(store.addressInformation)) {
            searchString += " " + this.getStreetAddress(store) + " " + this.getCityAndPostalCode(store);
        }
        return _.isEmpty(filter) || !_.isEmpty(searchString.match(new RegExp(filter, "gi")));
    }

    transformDeliveryOptions(options: Array<DropDownOption>): Array<DropDownOption> {
        const withDeliveryOptions = _.map(options, (option) => {
            const location = _.get(option, "value.store.storeId.locationNumber");
            const addressesForOption = _.filter(
                this.props.deliveryAddresses,
                (address) => address.deliveryStore === location
            );
            if (addressesForOption.length > 1) {
                return this.createOptionsForMoreThanOneAddress(option, addressesForOption);
            } else if (addressesForOption.length === 1) {
                return [this.createOptionForOneAddress(option, addressesForOption[0])];
            } else {
                return [option];
            }
        });
        return _.flatMap(withDeliveryOptions);
    }

    /**
     * If the customer has more than one delivery address, we show the store as a headline and
     * the streets as options to be selected.
     */
    createOptionsForMoreThanOneAddress(
        option: DropDownOption,
        correspondingDeliveryAddresses: Array<FsdAddressType>
    ): Array<DropDownOption> {
        option.disabled = true;
        option.label = (
            <div>
                <i className="icon-big-delivery-address" />
                {" " + option.label}
            </div>
        );
        option.value.type = "address";

        const addressOptions = _.map(correspondingDeliveryAddresses, (address) => {
            return {
                value: { type: "address" as const, address: address, store: option.value.store },
                label: address.street,
                isAddress: true
            };
        });
        // Do not render store headline in the mobile version
        return this.props.inMobileMenu ? addressOptions : [option, ...addressOptions];
    }

    /**
     * If the customer has only one delivery address in a store, we create an option that
     * automatically selects the address associated to that store.
     */
    createOptionForOneAddress(option: DropDownOption, deliveryAddress: FsdAddressType): DropDownOption {
        return {
            value: { type: "address", address: deliveryAddress, store: option.value.store },
            label: option.label
        };
    }

    renderSelectedValue(option: DropDownOption) {
        return <div>{option.label}</div>;
    }

    handleSearchInputChange = (source: any) => {
        this.setState({
            searchInput: source.target.value
        });
    };

    handleSearchInputFocus = (ev: React.FocusEvent<HTMLElement>) => {
        ev.target.scrollIntoView({ behavior: "smooth" });
    };

    renderSearchbar() {
        const searchPlaceholder = this.context.intl.formatMessage({
            id: "EX_MOBILE_FILTER_METRO_STORES_PLACEHOLDER"
        });
        return (
            <InputGroup className="mfcss_input-group-inset input-group-block">
                <FormControl
                    type="text"
                    placeholder={searchPlaceholder}
                    value={this.state.searchInput}
                    onChange={this.handleSearchInputChange}
                    onFocus={this.handleSearchInputFocus as any /* the react-bootstrap type is wrong */}
                />
                <InputGroup.Button>
                    <Button bsStyle="link" className="btn-square">
                        <i className="icon-magnify icon-lg text-primary" />
                    </Button>
                </InputGroup.Button>
            </InputGroup>
        );
    }

    renderStoreSelectionHeadline() {
        const message = this.props.selectedOption
            ? "EX_GLOBAL_HEADER_SELECT_OTHER_STORE"
            : "EX_GLOBAL_HEADER_SELECT_STORE";
        return (
            <h3 className={"ex-address-search-header"}>
                <FormattedMessage id={message} />
            </h3>
        );
    }

    renderAddressOptionForMobile(address: FsdAddressType, option: DropDownOption) {
        return (
            <CustomerAddressBadgeWithStore
                key={address.hash}
                address={address}
                store={option.value.store}
                selected={
                    this.props.selectedOption &&
                    this.props.selectedOption.value.address &&
                    address.hash === this.props.selectedOption.value.address.hash
                }
                onClick={() => this.props.onChange(option)}
            />
        );
    }

    renderStoreOptionForMobile(selectedStoreId: string | undefined | null, option: DropDownOption) {
        const className =
            this.props.selectedOption && option.value.store.storeId.locationNumber === selectedStoreId
                ? "panel-primary background-primary-reduced-more"
                : option.className;
        return (
            <Panel
                key={"store_" + option.value.store.storeId.locationNumber}
                className={(className || "") + " text-brand sd-store-selection-option"}
                onClick={() => this.props.onChange(option)}>
                <strong>{option.label}</strong>
            </Panel>
        );
    }

    render() {
        const options = _.map(this.props.stores, (store) => ({
            value: { type: "store" as const, store: store },
            label: store.storeDisplayName
        }));
        const selectedStoreId = _.get(this.props.selectedOption, ["value", "store", "storeId", "locationNumber"]);
        const optionsOrderedAlphabetically = _.sortBy(options, ["label"]);
        const optionsWithEnhancedDeliveryStores = this.transformDeliveryOptions(optionsOrderedAlphabetically);
        if (this.props.inMobileMenu) {
            const filteredOptions = _.filter(optionsWithEnhancedDeliveryStores, (option) =>
                this.filterOption(option, this.state.searchInput)
            );
            return (
                <div>
                    <div className="background-primary-reduced-more ex-address-search-header-container">
                        {this.renderStoreSelectionHeadline()}
                        {this.renderSearchbar()}
                    </div>
                    {_.map(filteredOptions, (option) => {
                        const address = option.value.address;
                        if (option.value.type === "store") {
                            return this.renderStoreOptionForMobile(selectedStoreId, option);
                        } else if (address) {
                            return this.renderAddressOptionForMobile(address, option);
                        }
                        return null;
                    })}
                </div>
            );
        }

        return (
            <FormGroup className="no-margin">
                <Select
                    className="Select"
                    classNamePrefix="Select"
                    id="storesselector_stores"
                    styles={{
                        option: (provided, state) => {
                            return {
                                ...provided,
                                backgroundColor: state.isDisabled ? "#002d72" : provided.backgroundColor,
                                paddingLeft: state.data.isAddress ? "40px" : provided.paddingLeft
                            };
                        }
                    }}
                    options={optionsWithEnhancedDeliveryStores}
                    isOptionDisabled={(option) => !!option.disabled}
                    onChange={this.props.onChange}
                    filterOption={this.filterOption.bind(this) as any}
                    value={this.props.selectedOption}
                    resetValue={null}
                    placeholder={<FormattedMessage id="EX_GLOBAL_HEADER_SEARCH" />}
                    ignoreAccents={false}
                    valueRenderer={this.renderSelectedValue.bind(this)}
                    isClearable
                />
            </FormGroup>
        );
    }
}
